/**
 * HERMES — texto de tela dos processos seletivos (aluno, padrinho e admin). Dono: agente de
 * contrato (.spec/hermes/contrato.md §6). Rotulos iguais aos das constantes dos models da API
 * (ExamProcess, ExamProcessMilestone, ExamProcessDocument, ExamProcessRevision).
 */
import type {
  ExamProcessCategory,
  ExamProcessDocumentLinks,
  ExamProcessDocumentType,
  ExamProcessLifecycleStatus,
  ExamProcessMilestoneState,
  ExamProcessMilestoneType,
  ExamProcessResultKind,
  ExamProcessReviewStatus,
  ExamProcessRevisionSource,
} from '@/types/api'

export const EXAM_PROCESSES_TITLE = 'Processos seletivos'
/** Nome da area do aluno (menu e titulo), o mesmo da area do padrinho. */
export const APPROVAL_CENTER_TITLE = 'Central do Aprovado'
export const EXAM_PROCESSES_ROUTE = '/app/processos-seletivos'
export const ADMIN_EXAM_PROCESSES_ROUTE = '/admin/exam-processes'
export const ADMIN_EXAM_PROCESSES_TITLE = 'Cronogramas de processos'

/** Dias em que uma mudanca/retificacao ganha o destaque (igual a ExamProcess::RECENT_CHANGE_DAYS). */
export const RECENT_CHANGE_DAYS = 15
export const DEFAULT_TIMEZONE = 'America/Sao_Paulo'

/** Fusos brasileiros com o mesmo horario de Brasilia (sem horario de verao desde 2019): nao precisam de aviso. */
export const BRASILIA_TIME_TIMEZONES: ReadonlySet<string> = new Set([
  DEFAULT_TIMEZONE,
  'America/Bahia',
  'America/Fortaleza',
  'America/Recife',
  'America/Belem',
  'America/Maceio',
  'America/Araguaina',
  'America/Santarem',
])

/** Cidade de referencia dos fusos brasileiros fora do horario de Brasilia; o resto usa o nome IANA. */
export const TIMEZONE_CITY_LABELS: Readonly<Record<string, string>> = {
  'America/Manaus': 'Manaus',
  'America/Cuiaba': 'Cuiabá',
  'America/Campo_Grande': 'Campo Grande',
  'America/Porto_Velho': 'Porto Velho',
  'America/Boa_Vista': 'Boa Vista',
  'America/Rio_Branco': 'Rio Branco',
  'America/Eirunepe': 'Eirunepé',
  'America/Noronha': 'Fernando de Noronha',
}

/** Aviso do admin: processo nao nacional sem instituicao nao entra na lista de ninguem. */
export const HIDDEN_FROM_READERS_LABEL = 'Sem instituição: não aparece para padrinhos e alunos'

export const CATEGORY_LABELS: Readonly<Record<ExamProcessCategory, string>> = {
  RESIDENCIA_MEDICA: 'Residência médica',
  EXAME_NACIONAL: 'Exame nacional',
  POS_GRADUACAO_MEDICA: 'Pós-graduação médica',
}

/** Na ordem do ciclo de vida (filtros e select do admin). */
export const LIFECYCLE_LABELS: Readonly<Record<ExamProcessLifecycleStatus, string>> = {
  AWAITING_NOTICE: 'Aguardando edital',
  NOTICE_PUBLISHED: 'Edital publicado',
  REGISTRATION_OPEN: 'Inscrições abertas',
  REGISTRATION_CLOSED: 'Inscrições encerradas',
  NEXT_MILESTONE: 'Próximo marco',
  AWAITING_RESULT: 'Aguardando resultado',
  PRELIMINARY_RESULT_AVAILABLE: 'Resultado preliminar disponível',
  FINAL_RESULT_AVAILABLE: 'Resultado final disponível',
  SUSPENDED: 'Processo suspenso',
  CLOSED: 'Processo encerrado',
  FINISHED: 'Processo seletivo finalizado',
}

/** Na ordem cronologica tipica. */
export const MILESTONE_TYPE_LABELS: Readonly<Record<ExamProcessMilestoneType, string>> = {
  NOTICE_PUBLICATION: 'Publicação do edital',
  REGISTRATION_START: 'Início das inscrições',
  REGISTRATION_END: 'Término das inscrições',
  FIRST_PHASE: 'Primeira fase',
  SECOND_PHASE: 'Segunda fase ou prova prática',
  CURRICULUM_SUBMISSION: 'Envio curricular',
  CURRICULUM_ANALYSIS: 'Análise curricular',
  INTERVIEW: 'Arguição ou entrevista',
  PRELIMINARY_RESULT: 'Resultado preliminar',
  APPEALS: 'Recursos',
  FINAL_RESULT: 'Resultado final',
}

export const MILESTONE_STATE_LABELS: Readonly<Record<ExamProcessMilestoneState, string>> = {
  CONFIRMED: 'Confirmada',
  NOT_ANNOUNCED: 'Data ainda não divulgada',
  NOT_APPLICABLE: 'Não se aplica',
  VERIFYING: 'Em verificação',
  COMPLETED: 'Concluída',
  RECTIFIED: 'Alterada por retificação',
}

export const DOCUMENT_TYPE_LABELS: Readonly<Record<ExamProcessDocumentType, string>> = {
  NOTICE: 'Edital',
  RECTIFICATION: 'Retificação',
  EXAM_BOARD_PAGE: 'Página da banca',
  REGISTRATION_PAGE: 'Página de inscrições',
  RESULT: 'Resultado',
}

export const RESULT_KIND_LABELS: Readonly<Record<ExamProcessResultKind, string>> = {
  PRELIMINARY: 'Preliminar',
  FINAL: 'Final',
}

/** Os cinco botoes de documento, na ordem da tela. */
export const DOCUMENT_BUTTONS: ReadonlyArray<{ slot: keyof ExamProcessDocumentLinks; label: string }> = [
  { slot: 'notice', label: 'Abrir edital oficial' },
  { slot: 'latest_rectification', label: 'Ver retificação mais recente' },
  { slot: 'exam_board_page', label: 'Acessar página da banca' },
  { slot: 'registration_page', label: 'Ver página de inscrições' },
  { slot: 'result', label: 'Ver resultado oficial' },
]

export const REVIEW_STATUS_LABELS: Readonly<Record<ExamProcessReviewStatus, string>> = {
  pending: 'Pendente de aprovação',
  approved: 'Aprovada',
  rejected: 'Recusada',
}

export const REVISION_SOURCE_LABELS: Readonly<Record<ExamProcessRevisionSource, string>> = {
  agent: 'Enviada pelo agente',
  admin: 'Cadastrada no admin',
}

export const RECENT_CHANGE_LABEL = 'Mudança recente'
export const RECTIFIED_LABEL = 'Retificado'
export const NO_NEXT_MILESTONE_LABEL = 'Sem próximo marco divulgado'
export const DATE_NOT_ANNOUNCED_LABEL = 'Data ainda não divulgada'
export const SHOW_EXAM_PROCESS_DETAILS_LABEL = 'Ver mudanças, documentos e cronograma'
export const HIDE_EXAM_PROCESS_DETAILS_LABEL = 'Ocultar detalhes'

// D-013: o que o aluno acompanha fora da lista dele. Nome interno "radar" nao vai para a tela.
export const FOLLOW_OTHERS_LABEL = 'Acompanhar outras instituições'
export const STUDENT_LIST_TITLE = 'Suas instituições'
export const FOLLOWED_SECTION_TITLE = 'Você também acompanha'
export const FOLLOWED_SECTION_DESCRIPTION =
  'Ficam fora da sua lista de instituições prioritárias e não mudam seu TOP 5 nem o guia estatístico.'
export const FOLLOWED_LABEL = 'Acompanhando'
export const FOLLOWED_NATIONAL_EXAM_LABEL = 'Exame nacional'
export const FOLLOWED_NO_PROCESS_LABEL = 'Nenhum processo publicado no momento.'
export const UNFOLLOW_LABEL = 'Deixar de acompanhar'
export const FOLLOWED_SECTION_ERROR = 'Não foi possível carregar o que você acompanha.'
export const RETRY_LABEL = 'Tentar novamente'
export const FOLLOW_DIALOG_DESCRIPTION =
  'Escolha instituições ou exames nacionais com processo publicado. Eles aparecem na Central, separados da sua lista, e não mudam suas prioridades, o TOP 5 nem o guia estatístico.'
export const FOLLOW_SEARCH_LABEL = 'Busque por nome'
export const FOLLOW_ACTION_LABEL = 'Acompanhar'
export const FOLLOW_UNDO_LABEL = 'Desfazer'
export const FOLLOW_OPTIONS_LOADING = 'Carregando as opções...'
export const FOLLOW_OPTIONS_ERROR = 'Não foi possível carregar as opções.'
export const FOLLOW_OPTIONS_EMPTY =
  'Não há outras instituições ou exames com processo publicado para acompanhar agora.'
export const FOLLOW_SEARCH_EMPTY =
  'Nenhuma opção com esse nome. Aqui aparecem só instituições e exames com processo publicado que ainda não estão na sua lista nem no que você acompanha.'
export const FOLLOW_ADD_ERROR = 'Não foi possível acompanhar agora. Tente de novo.'
export const FOLLOW_OPTION_GONE = 'Esta opção não está mais disponível.'
export const FOLLOW_UNDO_ERROR = 'Não foi possível desfazer. Tente de novo.'
export const FOLLOW_DONE_LABEL = 'Concluir'
export const FOLLOW_CLOSE_LABEL = 'Fechar'
export const UNFOLLOW_TOAST_DESCRIPTION = 'Sua lista de instituições prioritárias não mudou.'
export const TRY_AGAIN_DESCRIPTION = 'Tente de novo.'
export const REFOLLOW_ERROR_DESCRIPTION = `Adicione de novo em ${FOLLOW_OTHERS_LABEL}.`
/** Toast com Desfazer fica mais tempo que o padrao de 5 s. */
export const UNDO_TOAST_DURATION_MS = 8000

export const unfollowAriaLabel = (name: string) => `${UNFOLLOW_LABEL} ${name}`
export const followAriaLabel = (name: string) => `${FOLLOW_ACTION_LABEL} ${name}`
export const undoFollowAriaLabel = (name: string) => `${FOLLOW_UNDO_LABEL}: ${name}`
export const unfollowedToast = (name: string) => `Você deixou de acompanhar ${name}`
export const refollowedToast = (name: string) => `Você voltou a acompanhar ${name}`
export const unfollowFailedToast = (name: string) => `Não foi possível deixar de acompanhar ${name}.`
export const refollowFailedToast = (name: string) => `Não foi possível voltar a acompanhar ${name}.`
export const followFailedToast = (name: string) => `Não foi possível acompanhar ${name}.`
